import type { HomeStateData } from '@workspace/api-backend'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { FocusCard } from '@workspace/ui/components/focus-card'
import { NotePanel } from '@workspace/ui/components/note-panel'
import type { ReactNode } from 'react'
import type { BannerKey } from '../config/copy'
import { ADD_FUNDS_AFTER_TOP_UP_COPY, ADD_FUNDS_STALLED_EARLY_WHY, HOME_COPY } from '../config/copy'
import { toneForState } from '../config/rail'
import { interpolate, tokensFor } from '../lib/interpolate'
import { HomeBanners } from './home-banners'
import { HomeProgressRail } from './home-progress-rail'
import { HomePulseStrip } from './home-pulse-strip'
import { renderHomeVisual } from './visuals'

export interface HomeWizardProps {
  data: HomeStateData
  onPrimary: () => void
  onGhost: () => void
  /**
   * Whether each button has somewhere to go. Copy and destinations live in two
   * maps, and both fields are optional, so a label without a target compiles
   * fine and renders a button that silently does nothing — this is what stops
   * that reaching the founder.
   */
  hasPrimary: boolean
  hasGhost: boolean
  onBanner: (banner: BannerKey) => void
  onPillLink: () => void
  /** "I'll do this later" on the Share card, when the state's copy has one. */
  onSnooze?: () => void
  /**
   * Swaps the primary button's label in place — used for the brief "Copied"
   * confirmation after `copy-share-link`, so the click gets the same feedback
   * the inline copy buttons in the card's own visual already give.
   */
  primaryLabel?: string
  /** Replaces the built-in visual — used for the in-card deck dropzone. */
  visualOverride?: ReactNode
}

/**
 * The whole of Home: where you are, the one thing to do, and how this week is
 * going.
 *
 * Deliberately one card. The founder gets a single next action rather than a
 * dashboard of competing widgets, which is the entire premise: the states this
 * replaces let a dead campaign look healthy because nothing was responsible for
 * saying what mattered most.
 */
export function HomeWizard({
  data,
  onPrimary,
  onGhost,
  hasPrimary,
  hasGhost,
  onBanner,
  onPillLink,
  onSnooze,
  primaryLabel,
  visualOverride,
}: HomeWizardProps) {
  const copy =
    data.key === 'ALERT_ADD_FUNDS' && data.addFundsAfterTopUp
      ? { ...HOME_COPY[data.key], ...ADD_FUNDS_AFTER_TOP_UP_COPY }
      : HOME_COPY[data.key]
  const tokens = tokensFor(data)
  const tone = toneForState(data.key)

  const why =
    data.key === 'ALERT_ADD_FUNDS' && data.addFundsStalledEarly
      ? ADD_FUNDS_STALLED_EARLY_WHY
      : copy.why

  return (
    <div className="mx-auto flex w-full max-w-[660px] flex-col gap-5 py-2">
      <HomeProgressRail stateKey={data.key} shareStepDone={data.shareStepDone} />

      <HomeBanners banners={data.banners as BannerKey[]} tokens={tokens} onAction={onBanner} />

      <FocusCard
        tone={tone}
        // The card re-resolves on a timer for the states that finish on their
        // own, so it can change with no user action. Without this a screen
        // reader never learns the wait ended.
        live
        eyebrow={copy.eyebrow}
        title={interpolate(copy.title, tokens)}
        titleAccent={interpolate(copy.titleAccent, tokens)}
        actions={
          <>
            {copy.cta && hasPrimary && (
              <CtaButton onClick={onPrimary}>
                {primaryLabel ?? interpolate(copy.cta, tokens)}
              </CtaButton>
            )}
            {copy.ghost && hasGhost && (
              <SecondaryButton onClick={onGhost}>{interpolate(copy.ghost, tokens)}</SecondaryButton>
            )}
            {copy.snooze && onSnooze && (
              <button
                type="button"
                onClick={onSnooze}
                className="ml-auto text-[12.5px] text-muted-foreground hover:text-foreground"
              >
                {copy.snooze}
              </button>
            )}
          </>
        }
        footnote={
          <>
            {copy.pill && (
              <span className="mr-1">
                {copy.pill.text}{' '}
                <button
                  type="button"
                  onClick={onPillLink}
                  className="underline underline-offset-2 hover:text-primary"
                >
                  {copy.pill.linkLabel}
                </button>
                .
              </span>
            )}
            <span className="text-muted-foreground">
              <b className="font-semibold text-foreground/80">Next</b>{' '}
              {interpolate(copy.next, tokens)}
            </span>
          </>
        }
      >
        {/* Visual first, then the reasoning — the thing being explained before
            the explanation. `why` used to be the card's lede, which put it
            above the visual and gave it the same weight as the headline. */}
        <div className="flex flex-col gap-4">
          {visualOverride ?? renderHomeVisual(data)}
          {/* With the campaign's own numbers rather than stranded at the foot of
              the page: lifetime first, then the week, which is how the mock
              reads and how a founder asks it — "how far along, and how has it
              been going lately". */}
          <HomePulseStrip pulse={data.pulse} sentTotal={data.sentTotal} />
          <NotePanel label="Why this matters" tone={tone}>
            {interpolate(why, tokens)}
          </NotePanel>
        </div>
      </FocusCard>
    </div>
  )
}
